import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Canvas from './Canvas';
import { usePedigreeInstance } from './pedigree/instanceContext';
import { useWorkspaceActions } from 'src/store/WorkspaceContext';
import { deriveProxyNodes, proxyIntentsOf } from './pedigree/deriveProxies';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Popper,
  Tooltip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AddCircle,
  ErrorOutlineOutlined,
  Info,
  Keyboard,
  OpenWith,
  PeopleAlt,
  Redo,
  RemoveCircle,
  Undo
} from '@mui/icons-material';
import * as d3 from 'd3';
import ContextMenu from './canvas-tools/ContextMenu';
import EdgeContextMenu from './canvas-tools/EdgeContextMenu';
import { SIDEBAR_VALUE } from '../pedigree-sidebar/util';
import { readLabReportInboxLink } from '../pedigree-sidebar/sections/clinical-notes-section/labReportInboxLink';
import { AppContext, ThemeContext, UserContext } from 'src/store/ContextStore';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS, PEDIGREE } from 'src/components/Constants';
import { BRAND_COLOURS, CLINICAL_COLOURS } from 'src/theme/palette';
import {
  EVENTS,
  getUniqueDiagnoses,
  getUniquePhenotypes,
  getUniqueGenotypes
} from 'src/components/admin/appReducer';
import PedigreeSideBar from '../pedigree-sidebar/PedigreeSideBar';
import LocatePerson from './canvas-tools/LocatePerson';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import CanvasPreferences from './canvas-tools/CanvasPreferences';
import ClearPosition from './canvas-tools/ClearPosition';
import AutoArrange from './canvas-tools/AutoArrange';
import {
  PedigreeSaveIndicator,
  ENABLE_STACKED_SAVE_INDICATOR
} from './canvas-tools/PedigreeSaveIndicator';
import { config as layoutConfig, isReproNode } from './pedigree/config';
import { getAllowedRelativeActions, RELATIVE_ACTIONS } from './pedigree/utils';
import {
  addSon,
  addDaughter,
  addUnknown,
  addSpouse,
  addBrother,
  addSister,
  addParents,
  addGrandparents
} from './canvas-tools/options/addRelativeActions';
import { stripMemberReferencesToPid } from './pedigree/edgeIntegrity';
import {
  resolveShortcut,
  isEditableTarget,
  PANELS_NEEDING_NODE,
  FAMILY_ONLY_PANELS
} from './keyboardShortcuts';
import ShortcutsHelp from './canvas-tools/ShortcutsHelp';
import LockBanner from './canvas-tools/LockBanner';
import BulkEditModal from '../BulkEditModal';
import MergePersonSelectModal from 'src/components/merge-person/MergePersonSelectModal';
import MergePersonWizard from 'src/components/merge-person/MergePersonWizard';
import MigrateModal from '../MigrateModal';
import DeleteNode from './canvas-tools/options/DeleteNode';
import {
  getEligiblePids,
  getComponentOf,
  getMigratablePidsDetail
} from './migrateUtil';
import { getProbandState } from './pedigree/probandState';
import { canDeleteNode } from './pedigree/nodeActionGates';
import {
  resolveSelf,
  hasParents as hasParentsOf,
  hasGrandparents as hasGrandparentsOf
} from './pedigree/relativePredicates';
import { MENU_MODIFIERS } from './canvas-tools/menuTheme';
import { selectionPromptFor } from './selectionPrompt';
import { ownerPidOf } from './selectionGuard';
import useSelectionGuard from './useSelectionGuard';
import { canOpenNodeMenu, canOpenReproEditor } from './nodeInteractionPolicy';
import { clearSelectionHighlight } from './selectionHighlight';
import { pruneUnresolvedProxyIds } from './proxyIdCleanup';
import { usePruneOrderForStructuralEdit } from './structuralEditContext';
import {
  useShortcutsEnabled,
  toggleShortcutsEnabled
} from './shortcutsEnabledStore';
import { isAnonymousActive } from '../anonymousPedigrees';
import { useQueryClient } from '@tanstack/react-query';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const ChartBox = forwardRef(function ChartBox(
  {
    // TGCLOUD-1669 Phase 7. Set when this instance backs a PERSON tab: same
    // family, same persistence, no canvas, and this member selected on arrival.
    personMemberId = null,
    // This session's persisted view state, read once at mount.
    initialViewState,
    // Pass-through: the person tab's badge. See PedigreeChartView.
    onPersonLockStateChange,
    // Pass-through: the sidebar's unsaved sections, for the tab strip's close
    // guard (TGCLOUD-1839). See PedigreeChartView.
    onUnsavedSectionsChange,
    pedRef,
    user,
    memberRecords,
    canvasMembers,
    carrier = {},
    // TGCLOUD-1793 (1504 B4): canonical disease -> colour, pass-through to Canvas.
    diseaseColorMap = {},
    carrierStyle = {},
    setMemberRecords,
    setPedigreeState,
    labelsSet,
    setLabelsSet,
    onOpenLegendConfig,
    options,
    setOptions,
    colors,
    patient,
    customPositions,
    setCustomPositions,
    establishedOrder,
    setEstablishedOrder,
    orderLocked = false,
    setOrderLocked = null,
    isLocked,
    isMine,
    lockState,
    requestStatus,
    onRequestLock,
    onCancelRequest,
    onForceAcquireLock,
    pendingRequest,
    onRespondToRequest,
    personLockHook,
    isMigrated,
    onSidebarChange,
    analysis,
    // Data overlays (TGCLOUD-1670). Results ride in a ref and repaint on a
    // version bump, deliberately not through props that would re-run Canvas's
    // render effect (and, through it, a relayout) on every criteria tweak.
    onOpenOverlays = null,
    onStepOverlayMatch = null,
    dataOverlaySlotsRef = null,
    dataOverlayStyleRef = null,
    dataOverlayVersion = 0,
    linkStatusMap = {},
    refetchLinkStatus,
    isSaving = false,
    // The last save attempt did not reach the server — refused by the backend
    // (409/413), or never sent because a colleague holds the family lock. It
    // SUPPRESSES the transient indicator rather than sitting beside it: that
    // component flashes "Saved just now" purely on `isSaving` going false, so
    // leaving it mounted would put a green tick over a write that did not land.
    saveFailed = false,
    onHistoryCheckpoint = null,
    onAutomaticLayoutWrite = null,
    onHistoryUndo = null,
    onHistoryRedo = null,
    canUndo = false,
    canRedo = false,
    viewHide = null
  },
  ref
) {
  const t = useAutoTranslation();

  const { theme } = React.useContext(ThemeContext);
  const { user: loggedInUser } = React.useContext(UserContext);
  const { dispatchUpdate, content } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  // Merge wizard is name-driven (person search + PII details step) — no
  // entry point in anonymous mode; the backend strips the commit anyway.
  const anonymousPedigrees = isAnonymousActive(
    content?.settings?.pedigreeSetting
  );

  const [menu, setMenu] = React.useState(false);
  // TGCLOUD-1909: a relayout after the first is in flight (the Canvas reports it);
  // the arranging chip shows under the editing banner, centred the same way.
  const [arranging, setArranging] = React.useState(false);
  const brand = useTheme()?.palette?.brand ?? BRAND_COLOURS;
  const [anchorEl, setAnchorEl] = React.useState(null);

  const [edgeMenu, setEdgeMenu] = React.useState(null);
  const [edgeAnchorEl, setEdgeAnchorEl] = React.useState(null);
  const pruneOrderForStructuralEdit = usePruneOrderForStructuralEdit();

  const proxies = React.useMemo(
    () => proxyIntentsOf(memberRecords),
    [memberRecords]
  );

  const canvasMembersWithProxies = React.useMemo(
    () => deriveProxyNodes(canvasMembers, proxies),
    [canvasMembers, proxies]
  );

  const handleAddProxy = React.useCallback(
    (intent) => {
      if (intent?.originalPid == null || intent?.proxyId == null) return;
      setMemberRecords((prev) =>
        (prev || []).map((m) => {
          if (m.pid !== intent.originalPid) return m;
          const existing = Array.isArray(m.proxyIds) ? m.proxyIds : [];
          if (existing.includes(intent.proxyId)) return m;
          return { ...m, proxyIds: [...existing, intent.proxyId] };
        })
      );
      setPedigreeState?.((s) => ({
        ...s,
        save: PEDIGREE.saveStatus.IN_PROGRESS
      }));
    },
    [setMemberRecords, setPedigreeState]
  );

  const handleRemoveProxy = React.useCallback(
    (intent) => {
      if (intent?.originalPid == null || intent?.proxyId == null) return;
      setMemberRecords((prev) =>
        (prev || []).map((m) => {
          if (m.pid !== intent.originalPid) return m;
          const existing = Array.isArray(m.proxyIds) ? m.proxyIds : [];
          if (!existing.includes(intent.proxyId)) return m;
          return {
            ...m,
            proxyIds: existing.filter((id) => id !== intent.proxyId)
          };
        })
      );
      setPedigreeState?.((s) => ({
        ...s,
        save: PEDIGREE.saveStatus.IN_PROGRESS
      }));
    },
    [setMemberRecords, setPedigreeState]
  );
  // Drop proxyIds that no longer name a visible spouse. This is a RENDER-TIME
  // REPAIR of data the user did not touch, so it deliberately does NOT arm a
  // save (TGCLOUD-1801) — see pruneUnresolvedProxyIds for why that mattered and
  // why the logic lives outside this component now.
  //
  // Of the two ways to break the arming chain this is the safe one. Making
  // onEdit('data') respect the interaction gate instead would apply to every
  // clinical save, and PedigreeChartView is explicit that gating 'data' can
  // only fail in the dangerous direction — real record edits committed without
  // the lock.
  //
  // Cost of not arming: the repair stays in memory until the user's next real
  // edit persists it. It is idempotent and re-derived on every load, so a
  // pedigree that is only ever viewed simply repairs itself again next time,
  // which is the correct outcome for a family nobody edited.
  React.useEffect(() => {
    const cleaned = pruneUnresolvedProxyIds(memberRecords);
    if (cleaned) setMemberRecords(cleaned);
  }, [memberRecords, setMemberRecords]);

  // Active-tab flag from the workspace host: global window keyboard shortcuts
  // (Ctrl+A select-all, arrow-nudge, undo/redo) must act only on the visible
  // chart, never on the other charts kept mounted behind it. Defaults to true for
  // a lone chart (no provider), so single-pedigree behaviour is unchanged.
  const { isActive, instanceId } = usePedigreeInstance();
  // ACTIONS only (TGCLOUD-1775). Subscribing to the whole workspace re-rendered
  // every mounted ChartBox on every activation, which is what kept a measured
  // tab switch committing all four open tabs after two earlier fixes. The one
  // piece of STATE this needed, the persisted view state, arrives as a prop
  // because it is read exactly once at mount.
  const workspace = useWorkspaceActions();
  const wsUpdateViewState = workspace?.updateViewState;
  const isActiveRef = React.useRef(isActive);
  React.useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // state for target node data
  const [data, setData] = useState(null);
  // state for target node
  const prevNode = React.useRef();

  const [sidebar, setSidebar] = useState({
    open: false,
    expand: false,
    expandAccordian: []
  });

  React.useEffect(() => {
    onSidebarChange?.({ open: sidebar.open, expand: sidebar.expand });
  }, [sidebar.open, sidebar.expand]); // eslint-disable-line react-hooks/exhaustive-deps

  const [error, setError] = React.useState(false);
  const [bulkEditOpen, setBulkEditOpen] = React.useState(false);
  const [clinicalRefreshKey, setClinicalRefreshKey] = React.useState(0);

  const legendSignature = React.useMemo(() => {
    if (!memberRecords?.length) return null;
    return JSON.stringify([
      getUniqueDiagnoses(memberRecords).map((d) => d.id),
      getUniquePhenotypes(memberRecords).map((p) => p.id),
      getUniqueGenotypes(memberRecords).map((g) => g.id)
    ]);
  }, [memberRecords]);

  React.useEffect(() => {
    if (!legendSignature) return;
    dispatchUpdate({
      type: EVENTS.REBUILD_PEDIGREE_LEGENDS,
      value: memberRecords
    });
  }, [legendSignature]);

  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  // Lifted out of local state (shortcutsEnabledStore.js): the add controls now
  // advertise their bindings, and the sidebar's Add Relative panel renders from
  // PedigreeSideBar — a different subtree with no prop path from here.
  const shortcutsEnabled = useShortcutsEnabled();

  const [selectionMode, setSelectionMode] = React.useState(false);
  const selectionModeRef = React.useRef(false);

  // Freehand / Move mode (Phase 3): an explicit opt-in toggle. When ON, dragging a node pins
  // it to an absolute pixel position (the legacy customPositions path) instead of reordering.
  // Session-only (not persisted per pedigree). The ref is read inside the d3 drag handler (so a
  // toggle takes effect without re-binding handlers); the state drives the button's pressed look.
  const [freehandMode, setFreehandMode] = React.useState(false);
  const freehandModeRef = React.useRef(false);
  const toggleFreehandMode = React.useCallback(() => {
    setFreehandMode((prev) => {
      const next = !prev;
      freehandModeRef.current = next; // keep ref in sync synchronously for the next drag
      return next;
    });
  }, []);

  // Person-merge state
  const [mergeSelectPersons, setMergeSelectPersons] = React.useState(null);
  const [mergeWizardPersons, setMergeWizardPersons] = React.useState(null);

  // Delete-confirmation target for the Delete/Backspace shortcut — the same
  // typed-UPN confirmation dialog the right-click "Delete" menu item opens.
  const [deleteNodeTarget, setDeleteNodeTarget] = React.useState(null);

  // Migrate-members state: holds the resolved member objects to migrate.
  const [migrateMembers, setMigrateMembers] = React.useState(null);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectedIdsRef = useRef(new Set());
  React.useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  // UNSAVED SIDEBAR EDITS vs. A CHANGE OF SELECTION.
  //
  // The sidebar holds section edits in local state until its own Save, and
  // every path below that re-binds it to another record (a canvas click,
  // Locate, the lasso, Ctrl+A, the keyboard panels, a background click, the
  // family record, the sidebar's own "go to" links, a deep link) discarded
  // them without a word: PersonMenu re-seeds its form on every new `data`, and
  // Diseases and Phenotypes carried their pending picks over to the person
  // selected next. Each path now describes the selection it is about to make
  // and goes through `guardSelection`. That runs it at once unless the sidebar
  // holds unsaved edits AND would show a different record, in which case the
  // sidebar asks first ("Keep editing" / "Discard and switch", useDiscardGuard)
  // and runs it only after discarding. The rule is in selectionGuard.js.
  //
  // The mechanism is useSelectionGuard (this half) and the sidebar's
  // useDiscardGuard (the half that asks and discards). The sidebar's unsaved
  // sections pass through it on their way to the tab strip (TGCLOUD-1839).
  const dataRef = useRef(null);
  dataRef.current = data;
  const {
    unsavedSectionsRef,
    discardGuardRef,
    handleUnsavedSectionsChange,
    guardSelection,
    bindSelectedData
  } = useSelectionGuard({
    onUnsavedSectionsChange,
    selectedIdsRef,
    dataRef,
    setData
  });

  // FU9 — persist this tab's view state (selected node + which sidebar section is
  // open) up to the workspace, debounced, so a page refresh can restore it. Uses
  // the stable updateViewState callback (not the ws object) to avoid re-render
  // loops; only in workspace mode.
  React.useEffect(() => {
    if (!instanceId || !wsUpdateViewState) return;
    const t = setTimeout(() => {
      const selectedNodeId =
        selectedIds.size === 1 ? [...selectedIds][0] : null;
      wsUpdateViewState(instanceId, {
        selectedNodeId,
        sidebar: {
          open: !!sidebar.open,
          expandedSection: sidebar.expandAccordian
        }
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    instanceId,
    wsUpdateViewState,
    selectedIds,
    sidebar.open,
    sidebar.expandAccordian
  ]);

  // FU9 — on first mount of a RESTORED tab, re-apply the persisted selection so a
  // refresh returns to the node the clinician was on (the highlight effect paints
  // it once the nodes render). Runs once; reads the persisted view state present
  // at mount. Sidebar/camera visual restore is deferred (needs the node record +
  // live zoom; see PLAN).
  const viewStateRestoredRef = useRef(false);
  React.useEffect(() => {
    if (viewStateRestoredRef.current || !instanceId) return;
    viewStateRestoredRef.current = true;
    if (initialViewState?.selectedNodeId)
      setSelectedIds(new Set([initialViewState.selectedNodeId]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Lasso is disabled while a node is open (sidebar/menu showing) or while
  // selectionMode (Add Relation) is active, so its background drag doesn't
  // interrupt a user mid-task. Read by Canvas.jsx via the ref.
  // TGCLOUD-1457: lasso stays ENABLED in move (freehand) mode — selection tools must work
  // identically there (Kathryn lost multi-select the moment she toggled move mode). The
  // lasso is a shift+background drag, so it can't conflict with node drags.
  const lassoEnabledRef = useRef(true);
  React.useEffect(() => {
    lassoEnabledRef.current = !patient && !isLocked && !menu && !selectionMode;
  }, [patient, isLocked, menu, selectionMode]);

  // Also the cascade-screening picker and the overlay "select matches", through
  // applySelection below. A multi-selection hands the sidebar the family view,
  // so it is guarded like any other switch.
  const handleLassoSelect = React.useCallback(
    (pids) => {
      const list = Array.from(pids || []);
      guardSelection(
        () => {
          setMenu(false);
          setEdgeMenu(null);
          setSidebar({ open: false, expand: false, expandAccordian: [] });
          if (prevNode.current) {
            d3.select(prevNode.current).attr('stroke-width', 10);
            d3.select(prevNode.current).style('stroke', theme.appearance.text);
            setData(null);
          }
          setSelectedIds(new Set(list));
        },
        {
          next: {
            selectedCount: list.length,
            dataPid: prevNode.current ? null : dataRef.current?.pid ?? null
          }
        }
      );
    },
    [theme.appearance.text, guardSelection]
  );

  const location = useLocation();
  const appliedNavKeyRef = useRef(null);
  const navPidRef = useRef(null);
  // Canvas publishes its imperative actions (zoom, centerOn, invalidateLayout)
  // here; declared ahead of the deep-link effects, which pan to a person on an
  // already-settled chart.
  const canvasActionsRef = useRef({});
  // TGCLOUD-1863: the person the clinician arrived for, set by the deep-link
  // effects below BEFORE they select, and read by Canvas when it frames the open
  // (pedigree/openFrame.js focusPointFor); and the drawer width covering the
  // right of the canvas, read at frame time so that person is centred in the
  // uncovered area. Both are refs so the canvas memo below keeps its bailout.
  const focusPidRef = useRef(null);
  const viewInsetRef = useRef(0);
  React.useEffect(() => {
    // Mirrors workingAreaOffset: the drawer is 400 wide while open and not expanded.
    viewInsetRef.current = sidebar.open && !sidebar.expand ? 400 : 0;
  }, [sidebar.open, sidebar.expand]);
  const locationMemberId = location.state?.selectedMemberId ?? null;
  // The lab report inbox's link (TGCLOUD-1866 Phase 3) names its person in the address as well as
  // in the router state, so a link opened without the state (a new tab) still selects them. The
  // Test it opens waits for that selection (ClinicalNotes.jsx, labReportInboxLink.js), and is in
  // Clinical Records, which the link therefore expands.
  const labReportInboxLink = React.useMemo(
    () => readLabReportInboxLink(location.search),
    [location.search]
  );
  const locationGNum =
    location.state?.selectedFamilyMemberId ??
    labReportInboxLink?.familyMemberId ??
    null;

  React.useEffect(() => {
    prevNode.current = null;
    appliedNavKeyRef.current = null;
    setData(null);
    setSidebar({ open: false, expand: false, expandAccordian: [] });
  }, [user?._id]);

  React.useEffect(() => {
    const navKey = locationGNum || locationMemberId;
    if (
      !navKey ||
      appliedNavKeyRef.current === navKey ||
      memberRecords.length === 0
    )
      return;
    const member = locationGNum
      ? memberRecords.find(
          (m) => m.pid !== 1 && m.familyMemberId === locationGNum
        ) ||
        memberRecords.find(
          (m) =>
            m.familyMemberId === locationGNum || m.proBandId === locationGNum
        )
      : memberRecords.find((m) => String(m._id) === String(locationMemberId));
    if (member?.pid != null) {
      appliedNavKeyRef.current = navKey;
      focusPidRef.current = member.pid;
      guardSelection(
        () => {
          navPidRef.current = member.pid;
          setSelectedIds(new Set([member.pid]));
          bindSelectedData(member);
          setSidebar((prev) => ({
            ...prev,
            open: true,
            expandAccordian: [
              labReportInboxLink
                ? SIDEBAR_VALUE.clinicalNotes
                : SIDEBAR_VALUE.recordStatus
            ]
          }));
        },
        { next: { selectedCount: 1, dataPid: member.pid } }
      );
      // An already-settled chart (the family was open in another tab, or the
      // link arrived after the open): pan to the person now. Before the first
      // layout this is a no-op, and the open itself frames on focusPidRef.
      canvasActionsRef.current?.centerOn?.(member.pid, { silent: true });
    }
  }, [
    memberRecords,
    locationMemberId,
    locationGNum,
    labReportInboxLink,
    guardSelection,
    bindSelectedData
  ]);

  // TGCLOUD-1669 Phase 7. A person tab arrives knowing WHICH member it is for
  // and nothing else, so it selects that member itself as soon as the family
  // has been derived. This is the same mechanism the existing deep link uses
  // (find in memberRecords, setData, setSelectedIds), which is what proves
  // selection needs no canvas.
  //
  // Matched on `_id`, not on the G-number: a pid is a per-family small integer
  // and pid 1 is the proband of EVERY family, and familyMemberId can be
  // reassigned. A once-ref, so re-deriving memberRecords after a save does not
  // yank the clinician back to this member if they navigated to a relative.
  const seededPersonRef = React.useRef(null);
  React.useEffect(() => {
    if (!personMemberId || memberRecords.length === 0) return;
    if (seededPersonRef.current === personMemberId) return;
    const member = memberRecords.find(
      (m) => String(m._id) === String(personMemberId)
    );
    if (member?.pid == null) return;
    seededPersonRef.current = personMemberId;
    focusPidRef.current = member.pid;
    setSelectedIds(new Set([member.pid]));
    setData(member);
    setSidebar((prev) => ({ ...prev, open: true }));
  }, [personMemberId, memberRecords]);

  const urlHighlightGNum = React.useMemo(
    () => new URLSearchParams(location.search).get('highlight'),
    [location.search]
  );
  const appliedHighlightRef = useRef(null);
  React.useEffect(() => {
    if (
      !urlHighlightGNum ||
      appliedHighlightRef.current === urlHighlightGNum ||
      memberRecords.length === 0
    )
      return;
    const member =
      memberRecords.find(
        (m) => m.pid !== 1 && m.familyMemberId === urlHighlightGNum
      ) ||
      memberRecords.find(
        (m) =>
          m.familyMemberId === urlHighlightGNum ||
          m.proBandId === urlHighlightGNum
      );
    if (member?.pid != null) {
      appliedHighlightRef.current = urlHighlightGNum;
      focusPidRef.current = member.pid;
      guardSelection(
        () => {
          navPidRef.current = member.pid;
          setSelectedIds(new Set([member.pid]));
          bindSelectedData(member);
          setSidebar((prev) => ({ ...prev, open: true, expandAccordian: [] }));
        },
        { next: { selectedCount: 1, dataPid: member.pid } }
      );
      canvasActionsRef.current?.centerOn?.(member.pid, { silent: true });
    }
  }, [memberRecords, urlHighlightGNum, guardSelection, bindSelectedData]);

  // Person lock indicators: stable ref + version counter for Canvas.
  // lockedPersonsRef holds ALL locks; lockedByOtherRef holds only locks by other users.
  const lockedPersonsRef = useRef(new Map());
  const lockedByOtherRef = useRef(new Map());
  // TGCLOUD-1863: Locate person and the sidebar's "Open pedigree" go through
  // the canvas's own centre action (see canvasActionsRef above).
  const centerOnPerson = React.useCallback(
    (pid, opts) => canvasActionsRef.current?.centerOn?.(pid, opts),
    []
  );
  // A person tab's "Open pedigree" opens the chart ON that person: the workspace
  // action activates (or opens) the family's tab, and the navigation carries the
  // member in the router state, which the deep-link effect above reads exactly
  // as Search Family Records' View Pedigree is read.
  const navigate = useNavigate();
  const openPedigreeOnPerson = React.useCallback(
    (familyId, { focusMemberId = null } = {}) => {
      workspace?.openPedigree?.(familyId);
      if (familyId && focusMemberId) {
        navigate(CONSTANTS.pedigreeChartPath.replace(':_id', familyId), {
          state: { selectedMemberId: focusMemberId }
        });
      }
    },
    [workspace?.openPedigree, navigate] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // The node the user is currently acting on (selected / context-menu target). When an
  // add grows the tree, Canvas keeps THIS node fixed on screen so the clinician doesn't
  // lose focus of what they were editing (Phase A reanchor).
  const reanchorPidRef = useRef(null);
  // D7: one-shot orientation seed — Auto-arrange stores the order it is about to
  // clear so the next (unlocked) re-optimise keeps the displayed handedness.
  const orientationSeedRef = useRef(null);
  // TGCLOUD-1457: set by a pin-only drop (move-mode / group drag) so Canvas's layout
  // effect skips the pointless relayout (the drag already moved everything live).
  const skipPinRelayoutRef = useRef(false);

  useImperativeHandle(ref, () => ({
    // Both swap a person's sections for the family's, so both are switches.
    openFamilyRecord: () =>
      guardSelection(
        () => {
          setData(null);
          setSidebar((prev) => ({ ...prev, open: true }));
        },
        { next: { dataPid: null } }
      ),
    openBulkEdit: () => setBulkEditOpen(true),
    openFamilyHistory: () =>
      guardSelection(
        () => {
          setData(null);
          setSidebar((prev) => ({
            ...prev,
            open: true,
            expandAccordian: [SIDEBAR_VALUE.familyHistory],
            scrollTo: SIDEBAR_VALUE.familyHistory
          }));
        },
        { next: { dataPid: null } }
      ),
    // Programmatically set the multi-selection (e.g. the cascade-screening
    // candidate picker). Reuses the same path as the lasso so the result is
    // indistinguishable from a user-built shift-click selection.
    applySelection: (pids) => handleLassoSelect(Array.from(pids || [])),
    // Pan to one person without changing the zoom level. The data-overlay
    // results row steps through its matches with this.
    centerOn: (pid, opts) => canvasActionsRef.current?.centerOn?.(pid, opts),
    // UNDO/REDO cross-layer contract (plan v1.1 §2.1/§4): PedigreeChart's
    // applyLayoutState must invalidate any in-flight worker layout and stash the
    // outgoing order as a one-shot orientation seed — both live at/below ChartBox.
    invalidateLayout: () => canvasActionsRef.current?.invalidateLayout?.(),
    stashOrientationSeed: (order) => {
      orientationSeedRef.current = order;
    },
    // Asked by the canvas legends at drop time (PedigreeChart). A drop writes a
    // person's clinical record, and the sidebar disables that person's record
    // sections in exactly these two cases (sectionsAreDisabled).
    isPersonLockedByOther: (pid) =>
      pid != null && Boolean(personLockHook?.isLockedByOther?.(pid)),
    isBulkEditActive: () => bulkEditOpen
  }));

  // ARRANGE BEACON: the last layout reported a strictly-better from-scratch
  // arrangement exists → the Auto-arrange button pulses (user drives the change).
  const [arrangeHint, setArrangeHint] = React.useState(false);
  // TGCLOUD-1789 AC2: why a drop was refused. Set by Canvas at the moment of the drop,
  // before anything is written, so the user is not left watching a node snap back with
  // no explanation. Transient — this is guidance about one gesture, not chart state.
  const [reorderRefusal, setReorderRefusal] = React.useState(null);
  // Clear it after a few seconds. The id is held and cleared on unmount / on the next
  // message — a bare setTimeout here would be uncancellable, which is the leak
  // timerCleanup.test.js scans for (TGCLOUD-1710).
  React.useEffect(() => {
    if (!reorderRefusal) return undefined;
    const t = setTimeout(() => setReorderRefusal(null), 7000);
    return () => clearTimeout(t);
  }, [reorderRefusal]);
  const [lockedPersonsVersion, setLockedPersonsVersion] = React.useState(0);
  React.useEffect(() => {
    if (!personLockHook) return;
    lockedPersonsRef.current = personLockHook.lockedPersons;
    const userId = loggedInUser?._id ? String(loggedInUser._id) : null;
    const otherOnly = new Map();
    personLockHook.lockedPersons.forEach((lockInfo, pid) => {
      if (String(lockInfo.lockedByUserId) !== userId) {
        otherOnly.set(pid, lockInfo);
      }
    });
    lockedByOtherRef.current = otherOnly;
    setLockedPersonsVersion((v) => v + 1);
  }, [personLockHook?.lockedPersons]);

  // Sync data state with latest memberRecords when members change.
  React.useEffect(() => {
    const navPid = navPidRef.current;
    navPidRef.current = null;

    const targetPid = navPid ?? data?.pid;
    if (!targetPid) return;

    const updatedNode = memberRecords.find((m) => m.pid === targetPid);
    if (!updatedNode) return;

    if (navPid != null) {
      setData(updatedNode);
    } else if (data && data.pid) {
      const hasChanges =
        JSON.stringify({
          childrenIds: data.childrenIds,
          spouseIds: data.spouseIds,
          siblingIds: data.siblingIds,
          fatherId: data.fatherId,
          motherId: data.motherId,
          spouseRelationships: data.spouseRelationships,
          dob: data.dob
        }) !==
        JSON.stringify({
          childrenIds: updatedNode.childrenIds,
          spouseIds: updatedNode.spouseIds,
          siblingIds: updatedNode.siblingIds,
          fatherId: updatedNode.fatherId,
          motherId: updatedNode.motherId,
          spouseRelationships: updatedNode.spouseRelationships,
          dob: updatedNode.dob
        });

      if (hasChanges) {
        setData((prev) => ({ ...prev, ...updatedNode }));
      }
    }
  }, [memberRecords]);

  // Re-apply highlight after Canvas re-renders (multi-select aware)
  React.useEffect(() => {
    // Use setTimeout to ensure Canvas useEffect has completed rendering
    const timeoutId = setTimeout(() => {
      // Scope every node lookup to THIS chart's container. pids are per-family
      // and reused, so a document-wide selector would clear/repaint nodes in
      // another mounted chart (multi-pedigree-tabs). Falls back to `document`
      // for a lone chart before its container ref is wired.
      const root = pedRef?.current || document;
      // Clear all node outlines first
      d3.select(root)
        .selectAll('path.te')
        .attr('stroke-width', 10)
        .style('stroke', theme.appearance.text);
      // Re-apply lock indicators (red bold border) — only for locks held by others
      if (lockedByOtherRef.current?.size) {
        lockedByOtherRef.current.forEach((_lockInfo, pid) => {
          const gEl = root.querySelector(`g[data-selection-pid="${pid}"]`);
          if (gEl) {
            const pathEl = gEl.querySelector('path.te');
            if (pathEl) {
              d3.select(pathEl)
                .attr('stroke-width', 20)
                .style('stroke', CLINICAL_COLOURS.lockedPedigree);
            }
          }
        });
      }
      // Re-apply highlight for every selected node (selection overrides lock colour)
      selectedIds.forEach((pid) => {
        const gEl = root.querySelector(`g[data-selection-pid="${pid}"]`);
        if (gEl) {
          const pathEl = gEl.querySelector('path.te');
          if (pathEl) {
            d3.select(pathEl)
              .attr('stroke-width', 20)
              .style('stroke', theme.accent);
          }
        }
      });
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [memberRecords, selectedIds, lockedPersonsVersion, customPositions]);

  // Set-Relation pending completion: SetRelation hands us {node, relation,
  // complete} on entry; the second click is routed through handleClick (below)
  // so it survives any SVG rebuild (the old imperative d3 bind did not — a
  // mid-selection relayout froze all node clicks permanently).
  const pendingRelationRef = React.useRef(null);
  const handleSelectionExit = (state) => {
    // The payload goes into STATE as well as the ref: the ref drives the completion
    // click (and must not re-render), but the banner needs the relation and the
    // person's name to say which way round the link goes. Every other read of
    // selectionMode is a truthiness test, and an object is truthy.
    const payload = state && typeof state === 'object' ? state : null;
    setSelectionMode(payload || !!state);
    pendingRelationRef.current = payload;
  };

  // Wording lives in selectionPrompt.js — see that file for why the old text was
  // replaced and why direction is stated explicitly.
  const selectionPrompt = React.useMemo(
    () =>
      selectionPromptFor(
        selectionMode && typeof selectionMode === 'object'
          ? selectionMode
          : null
      ),
    [selectionMode]
  );

  const cancelSelectionMode = React.useCallback(() => {
    selectionModeRef.current = false;
    pendingRelationRef.current = null;
    setSelectionMode(false);
    // Fresh selection at call time — the entry highlight/fill may sit on a
    // rebuilt SVG. Scoped to this chart's container so it can't repaint another
    // mounted chart's background.
    d3.select(pedRef?.current || document)
      .select('.canvas')
      .style('fill', 'white');
    // ...and the NODE, which this used to leave mis-stroked: cancelling restored
    // the canvas fill and nothing else, so Esc or Cancel left the highlight on
    // the symbol permanently.
    clearSelectionHighlight(pedRef?.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // UNDO/REDO: one checkpoint per arrow-nudge BURST — the first nudge records the
  // pre-burst state, and the burst window resets after 400ms idle (plan §2.3). A
  // ref (not state) so held-key repeats never re-render or re-bind the handler.
  const nudgeBurstTimerRef = useRef(null);
  // An undo/redo replaces the state UNDER an open burst window: the next nudge
  // must start a fresh step (checkpoint again) or it would ride the stale window
  // and mutate with no checkpoint at all. Reset on every undo/redo trigger.
  const resetNudgeBurst = React.useCallback(() => {
    if (nudgeBurstTimerRef.current != null) {
      clearTimeout(nudgeBurstTimerRef.current);
      nudgeBurstTimerRef.current = null;
    }
  }, []);

  // Keyboard shortcuts: Escape, Ctrl+A, Ctrl/Cmd+Z / Ctrl+Y (undo/redo), arrow nudge
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // With several charts kept mounted (multi-pedigree-tabs), only the visible
      // one may respond to these window-level shortcuts — otherwise Ctrl+A /
      // arrow-nudge / undo would fire on every open chart at once. A lone chart is
      // always active (isActiveRef defaults true), so single-pedigree use is
      // unchanged.
      if (!isActiveRef.current) return;
      // Auto-repeat must never drive a mutation: holding a letter would otherwise
      // fire applyAdd dozens of times, and the keyboard path does not go through
      // AddRelativeMenu's `guarded` double-fire protection (develop's Phase 1
      // interaction hardening). Escape/undo/zoom are also fine to ignore on repeat.
      if (e.repeat) return;
      // Escape keeps its historical behavior even while a form field is focused —
      // the selection-mode banner instructs "press Escape", and it must work
      // mid-edit exactly as it did before the focus guard below existed.
      if (e.key === 'Escape') {
        if (selectionModeRef.current) {
          cancelSelectionMode();
          return;
        }
        // Clearing a multi-selection hands the sidebar back its person.
        guardSelection(() => setSelectedIds(new Set()), {
          next: { selectedCount: 0, dataPid: dataRef.current?.pid ?? null }
        });
        return;
      }
      // Never hijack the remaining shortcuts while the user is typing in a form
      // field (sidebar edit fields, dialogs, search boxes): before this guard,
      // Ctrl+A stole select-all from a focused input and selected every node.
      if (isEditableTarget(e.target)) {
        return;
      }
      // isEditableTarget was written when the only shortcuts were Ctrl/Cmd chords
      // ("before this guard, Ctrl+A stole select-all from a focused input").
      // TGCLOUD-1541 broadened the trigger set to ~20 BARE letters without
      // broadening the guard, so a letter pressed while a dialog is open — with
      // focus on the dialog rather than an input — reached the pedigree underneath
      // and mutated it. Treat any open modal/dialog as off-limits.
      if (
        e.target?.closest?.('[role="dialog"]') ||
        document.querySelector('.MuiModal-root, [role="dialog"]')
      ) {
        return;
      }
      const undoRedoZ =
        (e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z');
      const redoY = e.ctrlKey && !e.metaKey && e.key === 'y';
      if (layoutConfig.ENABLE_UNDO_REDO && (undoRedoZ || redoY)) {
        e.preventDefault();
        resetNudgeBurst();
        // Ctrl/Cmd+Z = undo; Ctrl/Cmd+Shift+Z and Ctrl+Y = redo. Plain Cmd+Y is
        // NOT claimed — it is the browser-history shortcut on macOS. The handlers
        // no-op when the pedigree is locked by another user / patient mode.
        if (e.key === 'y' || e.shiftKey) onHistoryRedo?.();
        else onHistoryUndo?.();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        guardSelection(
          () => setSelectedIds(new Set(canvasMembers.map((m) => m.pid))),
          {
            next: {
              selectedCount: canvasMembers.length,
              dataPid: dataRef.current?.pid ?? null
            }
          }
        );
      } else if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) &&
        selectedIds.size > 0 &&
        // Nudging writes customPositions, so it is a mutation and must respect the
        // same read-only gate as every other edit surface. See the isLocked note
        // below and in PedigreeChartView.
        !isLocked
      ) {
        e.preventDefault();
        if (onHistoryCheckpoint) {
          if (nudgeBurstTimerRef.current == null) onHistoryCheckpoint();
          else clearTimeout(nudgeBurstTimerRef.current);
          nudgeBurstTimerRef.current = setTimeout(() => {
            nudgeBurstTimerRef.current = null;
          }, 400);
        }
        // TGCLOUD-1457: Shift+arrow = FINE nudge (25px) for precise spacing adjustments;
        // plain arrow keeps the coarse two-grid-unit step.
        const step = e.shiftKey ? 25 : 200;
        const dx =
          e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy =
          e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        setCustomPositions((prev) => {
          const newMap = new Map(prev);
          selectedIds.forEach((pid) => {
            // Pins are keyed by UID everywhere (the engine looks them up by uid) — the
            // old code keyed by PID, so nudged positions never survived a re-render
            // (TGCLOUD-1457). Resolve the member's uid first.
            const member = canvasMembers.find((m) => m.pid === pid);
            const uid = member?.uid;
            if (uid == null) return;
            const existing = prev.get(uid);
            // A nudge needs a finite pixel base to add dx/dy to. A grid-only pin
            // ({gx,gy} with no legacy x/y) has none — the old code computed
            // existing.x + dx = NaN AND deleted gx (gap underivable), leaving a
            // {x: NaN, y: NaN} pin whose node then rendered with broken/missing
            // relationship lines (G-1-01391). Such pins fall through to the
            // DOM-read branch below, which nudges from the rendered position.
            if (
              existing &&
              Number.isFinite(existing.x) &&
              Number.isFinite(existing.y)
            ) {
              // D2: keep the grid-unit representation consistent through the
              // nudge (derive the pin's own gap from its stored pair); if that
              // isn't derivable (x/y of 0), drop to a pixel pin — Canvas's
              // one-shot upgrade re-adds units on the next layout pass.
              const nx = existing.x + dx;
              const ny = existing.y + dy;
              const next = { ...existing, x: nx, y: ny };
              if (existing.gx != null) {
                const gapX =
                  existing.x && existing.gx ? existing.x / existing.gx : null;
                if (gapX) next.gx = nx / gapX;
                else delete next.gx;
              }
              if (existing.gy != null) {
                const gapY =
                  existing.y && existing.gy ? existing.y / existing.gy : null;
                if (gapY) next.gy = ny / gapY;
                else delete next.gy;
              }
              newMap.set(uid, next);
            } else {
              // Read current position from DOM transform — scoped to this chart's
              // container so a reused pid can't read another mounted chart's node.
              const gEl = (pedRef?.current || document).querySelector(
                `g[data-selection-pid="${pid}"]`
              );
              if (gEl) {
                const t = gEl.getAttribute('transform') || '';
                const m = t.match(/translate\(([^,]+),([^)]+)\)/);
                if (m) {
                  newMap.set(uid, {
                    uid,
                    vx: 0,
                    vy: 0,
                    x: parseFloat(m[1]) + dx,
                    y: parseFloat(m[2]) + dy
                  });
                }
              }
            }
          });
          return newMap;
        });
      } else {
        const shortcut = shortcutsEnabled ? resolveShortcut(e) : null;
        // A proxy is a RENDER-only clone of a real member; the sidebar must be
        // bound to the OWNING record, exactly as the mouse path does in
        // handleClick. Passing the raw canvas node instead binds the editor to a
        // synthetic pid, so the clinician's edits are written against a member
        // that does not exist (silently discarded) and a person lock is taken on
        // that phantom pid. `canvasPid` keeps the highlight/un-highlight keyed to
        // the node actually on screen.
        const bindSidebarTo = (rawNode) => {
          if (!rawNode) return setData(null);
          const ownerPid = ownerPidOf(rawNode);
          const record = memberRecords.find((m) => m.pid === ownerPid);
          bindSelectedData({
            ...rawNode,
            ...(record || {}),
            canvasPid: rawNode.pid
          });
        };
        if (shortcut?.type === 'addRelative') {
          // isLocked is develop's single read-only enforcement point: it gates
          // onSumbit, hides Bulk edit, and disables undo/redo, lasso and the node
          // menus (see the comment in PedigreeChartView). The keyboard shortcuts
          // added a SIXTH mutation surface that was not on that list — without
          // this guard, a read-only FHQ preview or a pedigree locked by another
          // clinician could still be edited from the keyboard.
          if (isLocked) return;
          e.preventDefault();
          const pid = selectedIds.size === 1 ? [...selectedIds][0] : null;
          const rawNode =
            pid != null
              ? canvasMembersWithProxies.find((m) => m.pid === pid)
              : null;
          if (!rawNode && shortcut.relative !== 'unknown') return;

          const node = rawNode
            ? { ...rawNode, kind: layoutConfig.NODE_TYPE.NODE }
            : null;

          const allowedActions = getAllowedRelativeActions(node);
          // `movesExisting`: new parents or grandparents re-top a person who is
          // not connected to the proband (an FHQ "Other relative", a native
          // "Unknown person"), moving them down one or two rows, so their stored
          // rank goes in the SAME update (TGCLOUD-1786, structuralEditContext.js)
          // — as the context menu and the sidebar panel do. Running the action
          // again on the records it was computed from gives the same family:
          // only the uids of the new people differ, and they carry no rank.
          const applyAdd = (fn, { movesExisting = false } = {}) => {
            setMemberRecords((prev) => fn(prev, node, user));
            if (movesExisting) {
              pruneOrderForStructuralEdit(
                memberRecords,
                fn(memberRecords, node, user)
              );
            }
            setPedigreeState((prev) => ({
              ...prev,
              save: PEDIGREE.saveStatus.IN_PROGRESS
            }));
          };

          switch (shortcut.relative) {
            case 'son':
              if (allowedActions.has(RELATIVE_ACTIONS.son)) applyAdd(addSon);
              break;
            case 'daughter':
              if (allowedActions.has(RELATIVE_ACTIONS.daughter))
                applyAdd(addDaughter);
              break;
            case 'unknown':
              applyAdd(addUnknown);
              break;
            case 'spouse':
              if (allowedActions.has(RELATIVE_ACTIONS.spouse))
                applyAdd(addSpouse);
              break;
            // These three shared the shared predicate's THIRD hand-rolled copy
            // (TGCLOUD-1644 Phase 0 collapsed the other two). They also read the
            // laid-out node rather than resolving a proxy to its owner, which is
            // the divergence that module exists to prevent — latent here only
            // because a proxy's allowedActions excludes all three anyway.
            case 'brother': {
              if (
                hasParentsOf(resolveSelf(node, memberRecords)) &&
                allowedActions.has(RELATIVE_ACTIONS.brother)
              )
                applyAdd(addBrother);
              break;
            }
            case 'sister': {
              if (
                hasParentsOf(resolveSelf(node, memberRecords)) &&
                allowedActions.has(RELATIVE_ACTIONS.sister)
              )
                applyAdd(addSister);
              break;
            }
            case 'parents': {
              if (
                !hasParentsOf(resolveSelf(node, memberRecords)) &&
                allowedActions.has(RELATIVE_ACTIONS.parents)
              )
                applyAdd(addParents, { movesExisting: true });
              break;
            }
            case 'grandparents': {
              // Was a fourth inline copy, this one re-deriving hasGrandparents
              // from scratch inside an IIFE.
              if (
                !hasGrandparentsOf(
                  resolveSelf(node, memberRecords),
                  memberRecords
                ) &&
                allowedActions.has(RELATIVE_ACTIONS.grandparents)
              )
                applyAdd(addGrandparents, { movesExisting: true });
              break;
            }
            default:
              break;
          }
        } else if (shortcut?.type === 'toggleGrid') {
          e.preventDefault();
          setOptions((prev) => ({ ...prev, enable_grid: !prev?.enable_grid }));
        } else if (shortcut?.type === 'zoomIn') {
          e.preventDefault();
          canvasActionsRef.current?.zoomIn?.();
        } else if (shortcut?.type === 'zoomOut') {
          e.preventDefault();
          canvasActionsRef.current?.zoomOut?.();
        } else if (shortcut?.type === 'legendConfig') {
          // Legend visibility is a modal on the pedigree panel (TGCLOUD-1674),
          // so the shortcut asks the parent to open it rather than expanding a
          // sidebar panel that no longer exists.
          e.preventDefault();
          onOpenLegendConfig?.();
        } else if (shortcut?.type === 'overlays') {
          // Same shape as legendConfig: the popover belongs to the pedigree
          // panel, so the shortcut asks the parent to open it.
          e.preventDefault();
          onOpenOverlays?.();
        } else if (shortcut?.type === 'overlayStep') {
          // Silently inert when no overlay is on, which is the right answer for
          // a key that means "next match" when there are no matches.
          e.preventDefault();
          onStepOverlayMatch?.(shortcut.delta);
        } else if (shortcut?.type === 'help') {
          e.preventDefault();
          setShortcutsHelpOpen(true);
        } else if (shortcut?.type === 'panel') {
          const pid = selectedIds.size === 1 ? [...selectedIds][0] : null;
          const rawNode =
            pid != null
              ? canvasMembersWithProxies.find((m) => m.pid === pid)
              : null;
          if (!rawNode && PANELS_NEEDING_NODE.has(shortcut.panel)) return;
          e.preventDefault();
          const familyOnly = FAMILY_ONLY_PANELS.has(shortcut.panel);
          guardSelection(
            () => {
              if (familyOnly) {
                // LEGEND / LABELS live only in the family sidebar (TGCLOUD-1664), so
                // the shortcut has to deselect rather than bind to the person —
                // otherwise it would expand a panel the person view does not render
                // and appear to do nothing. Clearing selectedIds is enough to drop
                // the outline: the re-highlight effect wipes every `path.te` stroke
                // before re-applying, so no manual d3 reset is needed here.
                setSelectedIds(new Set());
                bindSidebarTo(null);
              } else {
                bindSidebarTo(rawNode || null);
              }
              setSidebar((prev) => ({
                ...prev,
                open: true,
                expandAccordian: [shortcut.panel]
              }));
            },
            {
              next: familyOnly
                ? { selectedCount: 0, dataPid: null }
                : {
                    selectedCount: selectedIds.size,
                    dataPid: ownerPidOf(rawNode)
                  }
            }
          );
        } else if (shortcut?.type === 'editName') {
          const pid = selectedIds.size === 1 ? [...selectedIds][0] : null;
          const rawNode =
            pid != null
              ? canvasMembersWithProxies.find((m) => m.pid === pid)
              : null;
          if (!rawNode) return;
          e.preventDefault();
          guardSelection(
            () => {
              bindSidebarTo(rawNode);
              setSidebar((prev) => ({
                ...prev,
                open: true,
                expandAccordian: [SIDEBAR_VALUE.person]
              }));
            },
            { next: { selectedCount: 1, dataPid: ownerPidOf(rawNode) } }
          );
        } else if (shortcut?.type === 'delete') {
          // Same read-only gate as the addRelative branch above: this opens the
          // confirm modal that issues a real server-side delete.
          if (isLocked) return;
          const pid = selectedIds.size === 1 ? [...selectedIds][0] : null;
          const rawNode =
            pid != null
              ? canvasMembersWithProxies.find((m) => m.pid === pid)
              : null;
          // ONE rule, shared with the right-click "Delete Person" gating in
          // ContextMenu (pedigree/nodeActionGates.js): proxies delete via a
          // different flow (Remove Proxy), and the active proband can't be
          // deleted directly.
          if (!canDeleteNode(rawNode, canvasMembers).allowed) return;
          e.preventDefault();
          setDeleteNodeTarget(rawNode);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedIds,
    canvasMembers,
    canvasMembersWithProxies,
    memberRecords,
    user,
    shortcutsEnabled,
    isLocked,
    cancelSelectionMode,
    onHistoryCheckpoint,
    onHistoryUndo,
    onHistoryRedo,
    resetNudgeBurst,
    pruneOrderForStructuralEdit,
    guardSelection,
    bindSelectedData
  ]);

  const closeMenus = () => {
    setMenu(false);
    setEdgeMenu(null);
    setEdgeAnchorEl(null);
  };

  const clearSelection = () => {
    setSidebar({
      open: false,
      expand: false,
      expandAccordian: []
    });

    if (prevNode.current) {
      d3.select(prevNode.current).attr('stroke-width', 10);
      d3.select(prevNode.current).style('stroke', theme.appearance.text);
      setData(null);
    }
    setSelectedIds(new Set());
  };

  // A click (or right-click) on the canvas background. It deselects and closes
  // the panel, which is a switch to the family view when a person is showing,
  // so it asks first when that would discard unsaved edits.
  const handleClose = () => {
    closeMenus();
    guardSelection(clearSelection, {
      next: {
        selectedCount: 0,
        dataPid: prevNode.current ? null : dataRef.current?.pid ?? null
      },
      confirmLabel: 'Discard and close'
    });
  };

  // The node context menu closing: after one of its actions, or on a click
  // away from it. The clinician was dismissing a MENU, not the panel, so with
  // unsaved edits the selection and the panel are simply left as they are
  // rather than asking about a close nobody requested.
  const handleMenuClose = () => {
    closeMenus();
    if (unsavedSectionsRef.current.length > 0) return;
    clearSelection();
  };

  // Where a click lands: the owning record for a proxy, the carrier for a
  // pregnancy or donor node (the sidebar is redirected to her, below).
  const findReproCarrier = (d) => {
    const recordId = String(d?.uid ?? '').split(':')[0];
    return (memberRecords || []).find((m) =>
      (m.pregnancies || []).some((p) => String(p._id) === recordId)
    );
  };
  const clickTargetPid = (d) => {
    if (layoutConfig.ENABLE_REPRO_NODES && isReproNode(d)) {
      return findReproCarrier(d)?.pid ?? d?.pid ?? null;
    }
    return ownerPidOf(d);
  };

  // Plain click: reset to single-node selection. Only ever run through the
  // guard in handleClick below.
  const selectClickedNode = (d, nodeRef, panels) => {
    if (prevNode.current) {
      d3.select(prevNode.current).attr('stroke-width', 10);
      d3.select(prevNode.current).style('stroke', theme.appearance.text);
    }
    // Allow sidebar to open for all non-patient users (even when pedigree is
    // locked by another — sidebar shows read-only view in that case)
    if (!patient && !selectionModeRef.current) {
      // Make selected node highlighted
      d3.select(nodeRef).attr('stroke-width', 20);
      d3.select(nodeRef).style('stroke', theme.accent);
      prevNode.current = nodeRef;
      const ownerPid = ownerPidOf(d);
      const record = memberRecords.find((m) => m.pid === ownerPid);

      // Reproductive nodes (pregnancy / gamete donor) are pedigree elements, not persons —
      // they get no person sidebar of their own. Clicking one redirects to the CARRIER's
      // sidebar with the Reproductive accordion expanded (mirrors person-click → sidebar),
      // so the pregnancy is editable in context. Resolve the carrier via the record id in
      // the node uid (pregnancy uid = record._id; donor uid = `<record._id>:<index>`).
      const isRepro = layoutConfig.ENABLE_REPRO_NODES && isReproNode(d);
      const carrier = isRepro ? findReproCarrier(d) : null;
      // ONE write of `data`. It used to be written with the clicked node and
      // then overwritten with the carrier, and keepSelectedData compares with
      // what is showing: clicking the pregnancy of the woman being edited
      // would have compared her with the intermediate and reset her form.
      bindSelectedData(
        carrier
          ? { ...carrier }
          : { ...d, ...(record || {}), canvasPid: d.pid, nodeRef: nodeRef }
      );
      setSelectedIds(new Set([carrier ? carrier.pid : d.pid]));

      if (isRepro) {
        // Undo the highlight just applied to the repro node; the selection effect
        // highlights the carrier from selectedIds instead.
        d3.select(nodeRef)
          .attr('stroke-width', 10)
          .style('stroke', theme.appearance.text);
        prevNode.current = null;
        if (carrier) {
          setSidebar((pre) => ({
            ...pre,
            open: true,
            expandAccordian: [SIDEBAR_VALUE.reproductive]
          }));
        } else {
          setSidebar((pre) => ({ ...pre, open: false }));
        }
        return;
      }

      // Which accordion to auto-expand for the person we just selected.
      // `sidebar-panel` remembers the last panel the user opened, but three
      // things can make it unusable, and each one used to open the sidebar with
      // nothing expanded at all:
      //   - it is absent (first ever selection);
      //   - it is the STRING "null" — PedigreeSideBar writes
      //     `setItem('sidebar-panel', null)` when a panel is COLLAPSED, and that
      //     stores "null", which is truthy, so `||` never fired;
      //   - it names LEGEND or LABELS, which TGCLOUD-1664 removed from the
      //     person view (they are family-sidebar controls now).
      // In all three, fall back to PERSON — the record the user selected the
      // node to see, and the item TGCLOUD-1664 puts at the top of the list.
      const remembered = localStorage.getItem('sidebar-panel');
      const rememberedIsUsable =
        !!remembered &&
        remembered !== 'null' &&
        remembered !== SIDEBAR_VALUE.legend &&
        remembered !== SIDEBAR_VALUE.labels;
      const prevPanel = rememberedIsUsable ? remembered : SIDEBAR_VALUE.person;

      setSidebar((pre) => ({
        ...pre,
        open: true,
        expandAccordian: panels ? panels : [prevPanel]
      }));
      // Signal for the Clinician Copilot guided walkthrough (read-only, no
      // payload beyond the type — no pid, no person data leaves this module).
      // Advances a walkthrough step whose advanceOn is 'node-selected'. No-op
      // when no tour is running.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('va:pedigree', { detail: { type: 'node-selected' } })
        );
      }
    }
  };

  const handleClick = (event, d, nodeRef, panels) => {
    // Remember the node being acted on so a subsequent add keeps it framed (Phase A).
    if (d?.pid != null) reanchorPidRef.current = d.pid;

    // Set-Relation second click: complete (or exit, on the same node) via the
    // persistent click path — rebuilt SVGs keep working. If the mode is somehow
    // active with no pending relation (legacy/orphaned state), recover by
    // cancelling instead of swallowing clicks forever.
    if (selectionModeRef.current) {
      const pending = pendingRelationRef.current;
      if (pending?.complete) {
        Promise.resolve(pending.complete(d)).finally(cancelSelectionMode);
      } else {
        cancelSelectionMode();
      }
      return;
    }

    const isModifier =
      event && (event.shiftKey || event.ctrlKey || event.metaKey);

    if (isModifier && !patient && !selectionModeRef.current && !isLocked) {
      // Toggle this node in the multi-selection. Growing it past one hands the
      // sidebar the family view, so that is guarded like any other switch.
      const selectedNow = selectedIdsRef.current;
      guardSelection(
        () =>
          setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(d.pid)) {
              next.delete(d.pid);
            } else {
              next.add(d.pid);
            }
            return next;
          }),
        {
          next: {
            selectedCount: selectedNow.has(d.pid)
              ? selectedNow.size - 1
              : selectedNow.size + 1,
            dataPid: dataRef.current?.pid ?? null
          }
        }
      );
      return;
    }

    // A patient-mode click only drops the old highlight; it selects nothing.
    if (patient) {
      selectClickedNode(d, nodeRef, panels);
      return;
    }
    guardSelection(() => selectClickedNode(d, nodeRef, panels), {
      next: { selectedCount: 1, dataPid: clickTargetPid(d) }
    });
  };

  // NOTE: canvasMembersWithProxies is declared once, higher up (just after
  // `proxies`). Both branches added an identical memo — develop here, sprint-94
  // earlier — and auto-merge kept both, which is a duplicate-declaration error.
  // The EARLIER position is the one that must survive: sprint-94's keyboard
  // shortcut effect lists it in a dependency array that is evaluated during
  // render well above this line, so declaring it only here throws
  // "Cannot access 'canvasMembersWithProxies' before initialization".
  const eligiblePids = React.useMemo(
    () => getEligiblePids(canvasMembersWithProxies),
    [canvasMembersWithProxies]
  );

  // REQ-I6 navigation. The sidebar can ask for a member to be SELECTED — "go to the child born of
  // this pregnancy", or back to the carrier whose record this person was born of. Selection lives
  // here (data + selectedIds + the canvas highlight), so the sidebar posts a pid and this performs
  // it, then clears the request so it fires once.
  React.useEffect(() => {
    const pid = sidebar?.selectPid;
    if (pid == null) return;
    setSidebar((pre) => ({ ...pre, selectPid: null }));
    const member = (memberRecords || []).find((m) => m.pid === pid);
    if (!member) return;
    guardSelection(
      () => {
        bindSelectedData({ ...member });
        setSelectedIds(new Set([pid]));
      },
      { next: { selectedCount: 1, dataPid: member.pid } }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebar?.selectPid, memberRecords]);

  // Double-click a fetus/pregnancy symbol → open THAT record's editor (REQ-H4; Desktop's own
  // confirmation tells the user to "double click on the fetus icon", so the affordance has to
  // exist). Rather than mounting a second copy of the editor here, this selects the carrier and
  // asks the sidebar's Reproductive accordion — the editor's existing, proven host — to open the
  // modal for that record. The single click has already run and selected the carrier, so the two
  // compose.
  const onReproNodeDoubleClick = (d) => {
    if (!canOpenReproEditor({ patient, isLocked })) return;
    const recordId = String(d?.uid ?? '').split(':')[0];
    if (!recordId) return;
    const carrier = (memberRecords || []).find((m) =>
      (m.pregnancies || []).some((p) => String(p._id) === recordId)
    );
    if (!carrier) return;
    guardSelection(
      () => {
        bindSelectedData({ ...carrier });
        setSelectedIds(new Set([carrier.pid]));
        setSidebar((pre) => ({
          ...pre,
          open: true,
          expandAccordian: [SIDEBAR_VALUE.reproductive],
          openReproRecordId: recordId
        }));
      },
      { next: { selectedCount: 1, dataPid: carrier.pid } }
    );
  };

  const onNodeClick = (e, d) => {
    if (
      !canOpenNodeMenu({
        patient,
        isLocked,
        selectionMode: selectionModeRef.current
      })
    )
      return;
    // Right-click / context-menu target is also an add anchor (Phase A reanchor).
    if (d?.pid != null) reanchorPidRef.current = d.pid;
    // Allow the menu with >2 selected only when the selection is a migratable
    // (fully independent, all-eligible) group — its only action is Migrate.
    if (selectedIds.size > 2) {
      const { independent } = getMigratablePidsDetail(
        canvasMembersWithProxies,
        selectedIds
      );
      if (!independent) return;
    }
    setMenu(d);
    setAnchorEl(e.currentTarget);
  };

  const handleMigrate = React.useCallback(
    (node) => {
      const pids =
        selectedIds.size > 0
          ? [...selectedIds]
          : [...getComponentOf(canvasMembersWithProxies, node?.pid)].map(
              (p) => {
                const n = Number(p);
                return Number.isNaN(n) ? p : n;
              }
            );
      const pidSet = new Set(pids.map((p) => String(p)));
      const resolved = (canvasMembers || []).filter((m) =>
        pidSet.has(String(m.pid))
      );
      setMenu(false);
      setAnchorEl(null);
      // Clinical-proband guard via the tri-state selector (see
      // pedigree/probandState.js): in the 'cleared' state probandPid is null
      // — there is no active proband to protect. The structural root stays
      // un-migratable regardless, via the independent migrateUtil eligibility
      // check below.
      const activeProbandPid = getProbandState(canvasMembers).probandPid;
      const probandPid =
        activeProbandPid != null ? String(activeProbandPid) : '';
      const probandInSelection =
        probandPid !== '' && resolved.some((m) => String(m.pid) === probandPid);
      if (probandInSelection) {
        setError(
          t(
            'The selection includes the proband which cannot be migrated. Deselect the proband and try again.'
          )
        );
        return;
      }
      const { independent } = getMigratablePidsDetail(
        canvasMembersWithProxies,
        new Set(pids)
      );
      if (!independent || resolved.length === 0) {
        const details = getMigratablePidsDetail(
          canvasMembersWithProxies,
          new Set(pids)
        );
        const ineligiblePids = pids
          .map((p) => String(p))
          .filter((p) => !details.eligible.has(p));
        const reasonParts = [];
        if (ineligiblePids.length > 0)
          reasonParts.push(`ineligible: ${ineligiblePids.join(', ')}`);
        if (details.danglingPids.length > 0)
          reasonParts.push(`linked-to: ${details.danglingPids.join(', ')}`);
        if (resolved.length === 0) reasonParts.push('no-resolved-members');
        setError(
          reasonParts.length > 0
            ? t(
                'Only a fully independent group (with no links to other members) can be migrated. Adjust your selection. ({{reasonParts}})',
                { reasonParts: reasonParts.join(' | ') }
              )
            : t(
                'Only a fully independent group (with no links to other members) can be migrated. Adjust your selection.'
              )
        );
        return;
      }
      setMigrateMembers(resolved);
    },
    [selectedIds, canvasMembers, canvasMembersWithProxies]
  );

  const refreshSourcePedigree = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });
    queryClient.invalidateQueries({ queryKey: ['probandUsers'] });
    window.dispatchEvent(new CustomEvent('clinical-notes-refresh'));
    axios
      .get(APICONSTANTS.getPedigreeDetails.replace(':familyId', user?.familyId))
      .then(({ data }) => {
        dispatchUpdate({
          type: EVENTS.SET_PEDIGREE_SELECTED_USER,
          value: {
            family: data.families,
            selectedUser: data.proband,
            perf: data.perf
          }
        });
      })
      .catch(() => {});
  }, [queryClient, user?.familyId, dispatchUpdate]);

  const onMigrateSuccess = React.useCallback(() => {
    const migratedPids = new Set((migrateMembers || []).map((m) => m.pid));
    if (migratedPids.size > 0) {
      setMemberRecords((prev) =>
        prev
          .filter((p) => !migratedPids.has(p.pid))
          .map((p) =>
            [...migratedPids].reduce(
              (member, pid) => stripMemberReferencesToPid(member, pid),
              p
            )
          )
      );
    }
    setMigrateMembers(null);
    setSelectedIds(new Set());
    refreshSourcePedigree();
  }, [migrateMembers, setMemberRecords, refreshSourcePedigree]);

  const handleMergeTwoSelected = React.useCallback(() => {
    if (anonymousPedigrees) return;
    const [pid1, pid2] = [...selectedIds];
    const node1 = canvasMembers.find((m) => m.pid === pid1);
    const node2 = canvasMembers.find((m) => m.pid === pid2);
    if (!node1 || !node2) return;
    const toPersonRef = (nodeData) => ({
      firstName: nodeData?.firstName,
      lastName: nodeData?.lastName,
      dob: nodeData?.dob,
      upn: nodeData?.upn,
      familyMemberId: nodeData?.familyMemberId || nodeData?.proBandId,
      familyId: user?.familyId
    });
    setMenu(false);
    setAnchorEl(null);
    setMergeSelectPersons({
      personA: toPersonRef(node1),
      personB: toPersonRef(node2)
    });
  }, [selectedIds, canvasMembers, user?.familyId, anonymousPedigrees]); // eslint-disable-line react-hooks/exhaustive-deps

  const onEdgeClick = (e, d) => {
    if (isLocked) return;
    setEdgeMenu(d);
    setEdgeAnchorEl(e.currentTarget);
  };

  const handleEdgeClose = () => {
    setEdgeMenu(null);
    setEdgeAnchorEl(null);
  };

  const handleCloseDialog = () => {
    setError(false);
  };

  const contextMenu = React.useMemo(() => {
    return (
      <ContextMenu
        user={user}
        node={menu}
        setMemberRecords={setMemberRecords}
        memberRecords={memberRecords}
        onCloseMenu={handleMenuClose}
        setPedigreeState={setPedigreeState}
        selectionModeRef={selectionModeRef}
        handleSelectionExit={handleSelectionExit}
        setError={setError}
        customPositions={customPositions}
        setCustomPositions={setCustomPositions}
        selectedCount={selectedIds.size}
        onMergeTwoSelected={handleMergeTwoSelected}
        onAddProxy={handleAddProxy}
        onRemoveProxy={handleRemoveProxy}
        eligiblePids={eligiblePids}
        onMigrate={handleMigrate}
        isSaving={isSaving}
        onHistoryCheckpoint={onHistoryCheckpoint}
        viewHide={viewHide}
        // The DRAWN member set: the hide-connector guard must analyse what the
        // canvas renders (an active proxy severs the original's drawn edges).
        hideAnalysisMembers={canvasMembersWithProxies}
      />
    );
  }, [
    user,
    menu,
    memberRecords,
    customPositions,
    selectedIds,
    viewHide,
    canvasMembersWithProxies,
    handleMergeTwoSelected,
    handleAddProxy,
    handleRemoveProxy,
    eligiblePids,
    handleMigrate,
    isSaving
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // linkStatusMap / refetchLinkStatus are lifted to PedigreeChart (which owns
  // the member list and renders the legend) so the legend and the canvas share
  // one source of truth and the legend can gate its swatches on presence.
  // A badge click selects the node and opens the sidebar Record Status panel —
  // mirrors the selectedFamilyMemberId deep-link (see the location.state effect).
  const onBadgeClick = (event, d, teEl) =>
    handleClick(event, d, teEl, [SIDEBAR_VALUE.recordStatus]);

  const canvas = React.useMemo(() => {
    return (
      <Canvas
        pedRef={pedRef}
        onArrangingChange={setArranging}
        proband={user}
        members={canvasMembersWithProxies}
        labelsSet={labelsSet}
        carrier={carrier}
        diseaseColorMap={diseaseColorMap}
        carrierStyle={carrierStyle}
        analysis={analysis}
        onNodeClick={onNodeClick}
        onReproNodeDoubleClick={onReproNodeDoubleClick}
        onEdgeClick={onEdgeClick}
        handleClick={handleClick}
        onCloseMenu={handleClose}
        options={options}
        colors={colors}
        customPositions={customPositions}
        setCustomPositions={setCustomPositions}
        establishedOrder={establishedOrder}
        setEstablishedOrder={setEstablishedOrder}
        orderLocked={orderLocked}
        setOrderLocked={setOrderLocked}
        freehandModeRef={freehandModeRef}
        selectedIdsRef={selectedIdsRef}
        lockedPersonsRef={lockedByOtherRef}
        lockedPersonsVersion={lockedPersonsVersion}
        dataOverlaySlotsRef={dataOverlaySlotsRef}
        dataOverlayStyleRef={dataOverlayStyleRef}
        dataOverlayVersion={dataOverlayVersion}
        onLassoSelect={handleLassoSelect}
        lassoEnabledRef={lassoEnabledRef}
        canvasActionsRef={canvasActionsRef}
        linkStatusMap={linkStatusMap}
        onBadgeClick={onBadgeClick}
        reanchorPidRef={reanchorPidRef}
        focusPidRef={focusPidRef}
        viewInsetRef={viewInsetRef}
        skipPinRelayoutRef={skipPinRelayoutRef}
        orientationSeedRef={orientationSeedRef}
        onArrangeHint={setArrangeHint}
        onReorderRefused={setReorderRefusal}
        onHistoryCheckpoint={onHistoryCheckpoint}
        onAutomaticLayoutWrite={onAutomaticLayoutWrite}
      />
    );
  }, [
    canvasMembersWithProxies,
    labelsSet,
    carrier,
    diseaseColorMap,
    carrierStyle,
    analysis,
    customPositions,
    establishedOrder,
    setEstablishedOrder,
    orderLocked,
    options,
    lockedPersonsVersion,
    dataOverlayVersion,
    handleLassoSelect,
    linkStatusMap,
    setArranging
  ]);

  const pedigreeSideBar = React.useMemo(() => {
    return (
      <PedigreeSideBar
        variant={personMemberId ? 'region' : 'drawer'}
        // A person tab knows WHICH member it is for before it knows anything
        // about them. Until the family arrives and the seeding effect finds
        // that member, `data` is null, and a null `data` means "family view" to
        // the sidebar. So the tab painted the eight FAMILY sections and then
        // swapped to the sixteen person ones a beat later.
        personPending={Boolean(personMemberId) && !data}
        onPersonLockStateChange={onPersonLockStateChange}
        onUnsavedSectionsChange={handleUnsavedSectionsChange}
        discardGuardRef={discardGuardRef}
        openPedigree={
          workspace?.openPedigree ? openPedigreeOnPerson : undefined
        }
        openPerson={workspace?.openPerson}
        data={selectedIds.size > 1 ? null : data}
        user={user}
        setSidebar={setSidebar}
        sidebar={sidebar}
        setData={setData}
        setLabelsSet={setLabelsSet}
        memberRecords={memberRecords}
        setMemberRecords={setMemberRecords}
        setPedigreeState={setPedigreeState}
        personLockHook={personLockHook}
        bulkEditActive={bulkEditOpen}
        pedigreeLockedByOther={isLocked && !isMine}
        isMigrated={isMigrated}
        analysis={analysis}
        onLinkStatusChanged={refetchLinkStatus}
        isSaving={isSaving}
        clinicalRefreshKey={clinicalRefreshKey}
      />
    );
  }, [
    clinicalRefreshKey,
    onPersonLockStateChange,
    handleUnsavedSectionsChange,
    workspace?.openPedigree,
    openPedigreeOnPerson,
    workspace?.openPerson,
    personMemberId,
    data,
    sidebar,
    labelsSet,
    memberRecords,
    selectedIds,
    bulkEditOpen,
    isLocked,
    isMine,
    isSaving,
    carrier,
    carrierStyle.activeStyle,
    analysis,
    refetchLinkStatus,
    // THE WHOLE HOOK, not `personLockHook?.lockedPersons`.
    //
    // THE DEFECT THIS FIXES. `lockedPersons` is one of SIX pieces of state the
    // sidebar's lock UI reads: the lock map, the per-person REQUEST STATUS, the
    // DECLINE MESSAGE and the pending incoming request are all separate state
    // in usePersonLocks. Keying the memo on the map alone meant that requesting
    // access — which changes only `requestStatuses` — produced a new hook object
    // that this cached element never saw. SidebarLockBanner went on rendering
    // the closures captured at the last `lockedPersons` change, so the button
    // never moved off "Request access" to "Requesting...", a decline message
    // never appeared, and the banner's ten-minute force-take timer (armed by a
    // `requestStatus === 'pending'` effect) never started at all — the requester
    // was left with no way forward short of reloading.
    //
    // Safe to depend on the whole object BECAUSE usePersonLocks memoises it
    // (TGCLOUD-1775): its identity changes when one of those state values
    // changes and at no other time, so this keeps every re-render the memo was
    // added to prevent — a tab switch still commits only the tabs whose active
    // flag moved.
    personLockHook
  ]);

  const enrichedPendingPersonRequest = React.useMemo(() => {
    const req = personLockHook?.pendingPersonRequest;
    if (!req) return null;
    const person = memberRecords.find(
      (m) => String(m.pid) === String(req.personId)
    );
    const personName =
      [person?.firstName, person?.lastName].filter(Boolean).join(' ') || null;
    const personUpn = person?.upn || null;
    return { ...req, personName, personUpn };
  }, [personLockHook?.pendingPersonRequest, memberRecords]);

  // Working-area offset: the region between the left rail and the docked detail
  // panel. Mirrors LockBanner's own bottom-banner offset (panel width 400 when
  // open and not expanded). Only applied to the top chip + stacked save status
  // when the flag is on, so the flag-off layout is unchanged.
  const workingAreaOffset = sidebar.open && !sidebar.expand ? 400 : 0;

  return (
    <Box
      sx={{
        height: '100%',
        position: 'relative'
      }}
    >
      {/* LOCK BANNERS — rendered on a person tab TOO (see canvasChrome).

          THE DEFECT THIS FIXES. This was inside the `!personMemberId` block
          below, and LockBanner is the ONLY renderer of the holder-side Grant
          and Decline controls for both `pendingRequest` (family lock) and
          `pendingPersonRequest` (person lock) — SidebarLockBanner is the
          REQUESTER's side. A person tab takes the person lock the moment it
          opens and the family lock on its first save, so a clinician working in
          a person tab was, routinely, a lock holder with no way to see that a
          colleague had asked for either lock: the request banner was suppressed
          along with the canvas chrome. The requester sat on "Requesting..." for
          the full ten minutes until the force-take timer let them take it.

          Only the "You are editing" chip is genuinely canvas-positioned (it is
          absolutely positioned over the working area and would float across the
          record), so that is all `canvasChrome` suppresses. The request banners
          are position:fixed along the bottom of the window and read correctly on
          either kind of tab. */}
      <LockBanner
        lockState={lockState}
        isMine={isMine}
        requestStatus={requestStatus}
        onRequestLock={onRequestLock}
        onCancelRequest={onCancelRequest}
        onForceAcquireLock={onForceAcquireLock}
        pendingRequest={pendingRequest}
        onRespondToRequest={onRespondToRequest}
        pendingPersonRequest={enrichedPendingPersonRequest}
        onRespondToPersonRequest={personLockHook?.respondToPersonRequest}
        sidebarOpen={sidebar.open}
        sidebarExpand={sidebar.expand}
        canvasChrome={!personMemberId}
        centerOffset={ENABLE_STACKED_SAVE_INDICATOR ? workingAreaOffset : 0}
      />
      {/* ARRANGING (TGCLOUD-1909): a relayout after the first can take seconds on
          a large family; this says so until the result lands. Placed and centred
          exactly as LockBanner's editing chip above it, in the brand teal. */}
      {arranging && !personMemberId && (
        <Box
          sx={{
            position: 'absolute',
            top: 40,
            left:
              ENABLE_STACKED_SAVE_INDICATOR && workingAreaOffset
                ? `calc(50% - ${workingAreaOffset / 2}px)`
                : '50%',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 1300
          }}
        >
          <Chip
            size="small"
            icon={<CircularProgress size={14} color="inherit" />}
            label={t('Arranging…')}
            sx={{
              bgcolor: alpha(brand.teal, 0.12),
              color: brand.tealAA,
              fontWeight: 'bold',
              boxShadow: 'none',
              '& .MuiChip-icon': { color: brand.tealAA }
            }}
          />
        </Box>
      )}
      {/* SAVE STATUS — rendered on a person tab TOO, for the same reason the
          lock banners above are.

          It used to sit inside the `!personMemberId` block below, and it is the
          ONLY persistent indicator that a write was refused ("Not saved"). A
          person tab edits the same memberRecords through the same autosave, so
          a clinician working in one had a refused save reported by a transient
          snackbar and nothing else — if they missed it, the tab showed no sign
          the record had not been written.

          Unlike the chip it stacks under, this is not canvas furniture: both
          states render nothing at all while idle (PedigreeSaveIndicator returns
          null, and `saveFailed` is false), so on a person tab it only appears
          while something is actually happening. It drops to the top of the
          region there because there is no "You are editing" chip above it, and
          takes no working-area offset because there is no canvas to centre on. */}
      {ENABLE_STACKED_SAVE_INDICATOR && (
        <Box
          sx={{
            position: 'absolute',
            top: personMemberId ? 8 : 38,
            left:
              !personMemberId && workingAreaOffset
                ? `calc(50% - ${workingAreaOffset / 2}px)`
                : '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            pointerEvents: 'none'
          }}
        >
          {saveFailed ? (
            <Box
              role="status"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'error.main',
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              <ErrorOutlineOutlined sx={{ fontSize: 16 }} aria-hidden />
              {t('Not saved')}
            </Box>
          ) : (
            <PedigreeSaveIndicator isSaving={isSaving} />
          )}
        </Box>
      )}
      {/* TGCLOUD-1789 — why a drop was refused. The drag is already undone by the time
          this shows (the node snapped home and nothing was saved), so this is the only
          account the user gets of it.

          Canvas-positioned, NOT a viewport Snackbar. A `Snackbar` anchored top-centre is
          `position: fixed`, which puts it under the application header — where QA found it
          obscured. Everything else that talks to the user about the canvas is absolutely
          positioned inside this container, so this follows that convention: centred on the
          WORKING area (so an open sidebar does not push it off-centre) and stacked clear of
          the "You are editing" chip at 8 and the save indicator at 38. */}
      {!personMemberId && reorderRefusal && (
        <Box
          sx={{
            position: 'absolute',
            top: 88,
            left: workingAreaOffset
              ? `calc(50% - ${workingAreaOffset / 2}px)`
              : '50%',
            transform: 'translateX(-50%)',
            zIndex: 2300,
            maxWidth: 'min(560px, calc(100% - 32px))'
          }}
        >
          <Alert
            severity="info"
            variant="filled"
            role="status"
            onClose={() => setReorderRefusal(null)}
            sx={{ boxShadow: 3 }}
          >
            {reorderRefusal}
          </Alert>
        </Box>
      )}
      {/* CANVAS CHROME. Suppressed on a person tab (TGCLOUD-1669 Phase 7):
          every item below is positioned over the canvas working area, so with
          no canvas they would float across the record. The person lock is still
          reported, by the sidebar's own SidebarLockBanner. */}
      {!personMemberId && (
        <>
          {selectedIds.size > 1 && (
            <Box
              sx={{
                position: 'absolute',
                // Push below the "You are editing" chip when isMine; otherwise sit at top
                top: lockState && isMine ? 48 : 8,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1300
              }}
            >
              <Chip
                icon={<PeopleAlt sx={{ fontSize: 16 }} />}
                data-va-anchor="pedigree.selection.chip"
                label={t('{{size}} members selected', {
                  size: selectedIds.size
                })}
                size="small"
                sx={{
                  bgcolor: '#e5f6fd',
                  color: '#014361',
                  fontWeight: 'bold',
                  '& .MuiChip-icon': { color: '#014361' }
                }}
              />
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              left: 15,
              top: 12,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              padding: '4px 8px'
            }}
          >
            <Tooltip title={t('Zoom Out')} arrow>
              <IconButton
                size="small"
                onClick={() => canvasActionsRef.current?.zoomOut?.()}
              >
                <RemoveCircle sx={{ fontSize: 22, color: '#3d4551' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('Zoom In')} arrow>
              <IconButton
                size="small"
                onClick={() => canvasActionsRef.current?.zoomIn?.()}
              >
                <AddCircle sx={{ fontSize: 22, color: '#3d4551' }} />
              </IconButton>
            </Tooltip>
            <CanvasPreferences
              options={options}
              setOptions={setOptions}
              members={canvasMembersWithProxies}
              inline
            />
            <LocatePerson
              pedref={pedRef}
              members={memberRecords}
              handleClick={handleClick}
              onCenter={centerOnPerson}
            />
            <ClearPosition
              customPositions={customPositions}
              setCustomPositions={setCustomPositions}
              memberRecords={memberRecords}
              onHistoryCheckpoint={onHistoryCheckpoint}
              onAutomaticLayoutWrite={onAutomaticLayoutWrite}
            />
            {layoutConfig.ENABLE_UNDO_REDO && (
              <>
                <Tooltip
                  title={
                    canUndo ? t('Undo layout change') : t('Nothing to undo')
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        resetNudgeBurst();
                        onHistoryUndo?.();
                      }}
                      disabled={!canUndo}
                      data-va-anchor="pedigree.toolbar.undo"
                      aria-label={t('Undo layout change')}
                    >
                      <Undo
                        sx={{
                          fontSize: 22,
                          color: canUndo ? '#3d4551' : undefined
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    canRedo ? t('Redo layout change') : t('Nothing to redo')
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        resetNudgeBurst();
                        onHistoryRedo?.();
                      }}
                      disabled={!canRedo}
                      data-va-anchor="pedigree.toolbar.redo"
                      aria-label={t('Redo layout change')}
                    >
                      <Redo
                        sx={{
                          fontSize: 22,
                          color: canRedo ? '#3d4551' : undefined
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {layoutConfig.ENABLE_ESTABLISHED_ORDER && (
              <AutoArrange
                establishedOrder={establishedOrder}
                customPositions={customPositions}
                setEstablishedOrder={setEstablishedOrder}
                setCustomPositions={setCustomPositions}
                setOrderLocked={setOrderLocked}
                orientationSeedRef={orientationSeedRef}
                onHistoryCheckpoint={onHistoryCheckpoint}
                // 2026-08-12 (Tim's ruling): the beacon lights whenever the
                // engine's from-scratch optimum is strictly better — INCLUDING a
                // hand-arranged (orderLocked) chart. The old `&& !orderLocked`
                // suppression meant one drag-to-reorder permanently blinded the
                // beacon until an Auto-arrange, so a user who deliberately
                // de-optimised the layout got no cue. The hint is advisory (the
                // user still decides when to adopt); a deliberate arrangement the
                // optimiser can't beat never lights it anyway.
                arrangeHint={arrangeHint}
              />
            )}
            {layoutConfig.ENABLE_ESTABLISHED_ORDER && (
              <Tooltip
                title={
                  freehandMode
                    ? t(
                        'Move mode ON — drag to reposition freely (click to return to reorder)'
                      )
                    : t('Move mode — drag a node to any position')
                }
                arrow
              >
                <IconButton
                  size="small"
                  onClick={toggleFreehandMode}
                  data-va-anchor="pedigree.toolbar.moveMode"
                  aria-pressed={freehandMode}
                >
                  <OpenWith
                    sx={{
                      fontSize: 22,
                      color: freehandMode ? '#1976d2' : '#3d4551'
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('Keyboard shortcuts')} arrow>
              <IconButton
                size="small"
                onClick={() => setShortcutsHelpOpen(true)}
                aria-label={t('Keyboard shortcuts')}
              >
                <Keyboard
                  sx={{
                    fontSize: 22,
                    color: shortcutsEnabled ? '#3d4551' : '#94a3b8'
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
      {pedigreeSideBar}
      {selectionMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 5,
            left: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 2200,
            px: 2
          }}
        >
          <Alert
            severity="warning"
            sx={{ fontWeight: 'bold' }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={cancelSelectionMode}
              >
                {t('Cancel')}
              </Button>
            }
          >
            {selectionPrompt}
          </Alert>
        </Box>
      )}
      {/* The layout worker is the expensive part of a chart, tens of seconds on
          a large family, and a person tab needs none of it. Suppressing the
          canvas is what makes a person tab cheap; everything above it in the
          chain (the family fetch, memberRecords, the debounced autosave, the
          locks) is reused unchanged. */}
      {!personMemberId && canvas}
      <BulkEditModal
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        memberRecords={
          selectedIds.size > 1
            ? memberRecords.filter((m) => selectedIds.has(m.pid))
            : memberRecords
        }
        setMemberRecords={setMemberRecords}
        setPedigreeState={setPedigreeState}
        setLabelsSet={setLabelsSet}
        familyId={user?.familyId}
        personLockHook={personLockHook}
        onClinicalRecordsSaved={() => setClinicalRefreshKey((k) => k + 1)}
      />
      <CustomDialog
        open={!!error}
        title={'Info'}
        handleClose={handleCloseDialog}
        minHeight={100}
        distanceTop={100}
        allowBackgroundClose={true}
        showCancelBtn={true}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Info fontSize="large" />
          {error}
        </Box>
      </CustomDialog>
      <ShortcutsHelp
        open={shortcutsHelpOpen}
        onClose={() => setShortcutsHelpOpen(false)}
        enabled={shortcutsEnabled}
        onToggle={toggleShortcutsEnabled}
      />
      {/* flip + preventOverflow: without them a right-click near the bottom or
          right of the viewport ran the menu off the screen. Pre-existing — the
          menu clipped at its old height too (TGCLOUD-1644). */}
      <Popper
        open={!!menu && !selectionMode}
        anchorEl={anchorEl}
        sx={{ zIndex: 1299 }}
        modifiers={MENU_MODIFIERS}
      >
        {contextMenu}
      </Popper>
      <Popper
        open={!!edgeMenu}
        anchorEl={edgeAnchorEl}
        sx={{ zIndex: 1299 }}
        modifiers={MENU_MODIFIERS}
      >
        <EdgeContextMenu
          edge={edgeMenu}
          user={user}
          memberRecords={memberRecords}
          setMemberRecords={setMemberRecords}
          // Without this a remove-relation on a family with no stored
          // topology was persisted nowhere (TGCLOUD-1813).
          setPedigreeState={setPedigreeState}
          onClose={handleEdgeClose}
        />
      </Popper>
      {deleteNodeTarget && (
        <DeleteNode
          node={deleteNodeTarget}
          user={user}
          memberRecords={memberRecords}
          setMemberRecords={setMemberRecords}
          handleClose={() => setDeleteNodeTarget(null)}
        />
      )}
      {mergeSelectPersons && (
        <MergePersonSelectModal
          open={!!mergeSelectPersons}
          personA={mergeSelectPersons.personA}
          personB={mergeSelectPersons.personB}
          onSelect={({ retained, deleted }) => {
            setMergeSelectPersons(null);
            setMergeWizardPersons({ retained, deleted });
          }}
          onClose={() => setMergeSelectPersons(null)}
        />
      )}
      {migrateMembers && (
        <MigrateModal
          open={!!migrateMembers}
          handleClose={() => setMigrateMembers(null)}
          members={migrateMembers}
          sourceUserId={user?._id}
          sourceFamilyId={user?.familyId}
          onSuccess={onMigrateSuccess}
        />
      )}
      {mergeWizardPersons && (
        <MergePersonWizard
          open={!!mergeWizardPersons}
          retained={mergeWizardPersons.retained}
          deleted={mergeWizardPersons.deleted}
          onClose={() => setMergeWizardPersons(null)}
          onSuccess={() => {
            const deletedIdentifier =
              mergeWizardPersons?.deleted?.familyMemberId;
            const deletedMember = deletedIdentifier
              ? memberRecords.find(
                  (m) =>
                    m.familyMemberId === deletedIdentifier ||
                    m.proBandId === deletedIdentifier
                )
              : null;
            if (deletedMember) {
              const deletedPid = deletedMember.pid;
              setMemberRecords((prev) =>
                prev
                  .filter((p) => p.pid !== deletedPid)
                  .map((p) => stripMemberReferencesToPid(p, deletedPid))
              );
            }
            setMergeWizardPersons(null);
            queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });
            queryClient.invalidateQueries({ queryKey: ['probandBrowse'] });
            queryClient.invalidateQueries({ queryKey: ['familySearchIndex'] });
            window.dispatchEvent(new CustomEvent('clinical-notes-refresh'));
            axios
              .get(
                APICONSTANTS.getPedigreeDetails.replace(
                  ':familyId',
                  user?.familyId
                )
              )
              .then(({ data }) => {
                dispatchUpdate({
                  type: EVENTS.SET_PEDIGREE_SELECTED_USER,
                  value: {
                    family: data.families,
                    selectedUser: data.proband,
                    perf: data.perf
                  }
                });
              })
              .catch(() => {});
          }}
        />
      )}
    </Box>
  );
});

export default ChartBox;
